import { Link } from "react-router-dom";
import {
  NJHeading,
  NJIcon,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { useCurrentUser } from "@/api/useCurrentUser";
import { userDisplayName } from "@/features/auth/userDisplayName";

type QuickLink = {
  to: string;
  icon: string;
  label: string;
  description: string;
};

// Shared "panel" style for the banner and the cards.
const panelClass =
  "border border-nj-neutral-subtle-default bg-nj-neutral-primary-default shadow-sm";

// Round branded icon badge; the size is passed via className.
function IconBadge({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={`flex items-center justify-center rounded-full bg-nj-brand-secondary-default text-nj-brand-solid-default ${className ?? ""}`}
    >
      <NJIcon name={name} />
    </span>
  );
}

export function HomePage() {
  const { data: user, isLoading } = useCurrentUser();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <NJSpinner aria-label="Loading" />
      </div>
    );
  }

  const isValidator = (user?.groups ?? []).some((group) =>
    /^agent-.+-validators$/.test(group),
  );
  const isAdmin = (user?.groups ?? []).includes("app-admin");

  const links: QuickLink[] = [
    {
      to: "/agents",
      icon: "storefront",
      label: "Agent Lib",
      description: "Discover available agents and their access status.",
    },
    {
      to: "/skills",
      icon: "inventory_2",
      label: "Skill store",
      description: "Browse the skill catalogue and propose new ones.",
    },
    {
      to: "/chat",
      icon: "chat",
      label: "Chat",
      description: "Open your conversations with agents.",
    },
    ...(isValidator
      ? [
          {
            to: "/access-requests",
            icon: "how_to_reg",
            label: "Access requests",
            description: "Review access requests awaiting validation.",
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            to: "/admin/users",
            icon: "admin_panel_settings",
            label: "Administration",
            description: "Manage users, groups, agents and skills.",
          },
        ]
      : []),
  ];

  return (
    <div className="flex w-full flex-col gap-8">
      <div className={`flex items-center gap-4 p-6 sm:gap-10 ${panelClass}`}>
        <IconBadge name="waving_hand" className="h-14 w-14 flex-shrink-0" />
        <div className="min-w-0">
          <NJHeading as="h1" scale="lg" className="break-words">
            Welcome, {userDisplayName(user)}
          </NJHeading>
          <NJText
            as="p"
            scale="sm"
            className="break-words text-nj-neutral-secondary-default"
          >
            Signed in as {user?.email ?? user?.login ?? "user"}
          </NJText>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={`flex flex-col gap-3 p-5 no-underline transition hover:border-nj-brand-moderate-default hover:shadow-md ${panelClass}`}
          >
            <IconBadge name={link.icon} className="h-10 w-10" />
            <NJHeading as="h2" scale="sm">
              {link.label}
            </NJHeading>
            <NJText
              as="p"
              scale="sm"
              className="text-nj-neutral-secondary-default"
            >
              {link.description}
            </NJText>
          </Link>
        ))}
      </div>
    </div>
  );
}
